import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Lock } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabaseClient'

/**
 * AccountSettings — currently only exposes a self-service password change
 * for the currently authenticated user. Uses the standard authenticated
 * Supabase flow:
 *   1. Re-authenticate with `supabase.auth.signInWithPassword` using the
 *      current session user's email + the "current password" the user just
 *      typed — this is the safe supported way to verify the current
 *      password on the client (never touches the Admin/service-role API).
 *   2. On success, call `supabase.auth.updateUser({ password })` to set
 *      the new password against the authenticated session.
 *
 * No password is ever logged, stored, or sent to our backend.
 */
const AccountSettings = () => {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const resetPasswords = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!user?.email) {
      setError('Chyba relácie. Prosím prihláste sa znova.')
      return
    }
    if (!currentPassword) {
      setError('Zadajte svoje súčasné heslo.')
      return
    }
    if (!newPassword || newPassword.length < 6) {
      setError('Nové heslo musí mať aspoň 6 znakov.')
      return
    }
    if (newPassword === currentPassword) {
      setError('Nové heslo sa musí líšiť od súčasného.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Nové heslá sa nezhodujú.')
      return
    }

    setLoading(true)
    try {
      // 1) Verify current password by re-authenticating the SAME user.
      //    On success, Supabase refreshes the session for the same user;
      //    role/permissions are unchanged.
      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      })
      if (reauthError) {
        setError('Súčasné heslo je nesprávne.')
        setLoading(false)
        return
      }

      // 2) Update the password on the authenticated user.
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      })
      if (updateError) {
        // Surface Supabase's own strength / policy messages, translated to SK
        // when we can, otherwise fall back to a safe generic message.
        const raw = String(updateError.message || '').toLowerCase()
        if (raw.includes('password') && (raw.includes('short') || raw.includes('at least'))) {
          setError('Nové heslo nespĺňa požiadavky (príliš krátke).')
        } else if (raw.includes('same') || raw.includes('different')) {
          setError('Nové heslo sa musí líšiť od súčasného.')
        } else if (raw.includes('weak')) {
          setError('Nové heslo je príliš slabé. Použite silnejšie heslo.')
        } else {
          setError('Nepodarilo sa zmeniť heslo. Skúste to prosím znova.')
        }
        setLoading(false)
        return
      }

      setSuccess('Heslo bolo úspešne zmenené.')
      resetPasswords()
    } catch (err) {
      setError('Nepodarilo sa zmeniť heslo. Skúste to prosím znova.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-transparent">
      <div className="container mx-auto px-4 py-8 md:py-10 max-w-2xl">
        <div className="mb-6">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="text-sm text-blue-700 hover:text-blue-800 inline-flex items-center gap-1"
            data-testid="account-back-btn"
          >
            <span aria-hidden="true">←</span> Späť na dashboard
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 ring-1 ring-slate-100 shadow-md shadow-slate-200/40 p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 inline-flex items-center justify-center ring-1 ring-blue-100">
              <Lock className="w-5 h-5" />
            </span>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Zmena hesla
            </h1>
          </div>
          <p className="text-sm text-gray-600 mb-6 ml-13">
            Prihlásený ako <span className="font-medium">{user?.email}</span>. Zmena hesla platí len pre tento účet.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="password-change-form">
            {/* Current password */}
            <div>
              <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-2">
                Súčasné heslo
              </label>
              <div className="relative">
                <input
                  id="current-password"
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full pl-3 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  autoComplete="current-password"
                  data-testid="current-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  aria-label={showCurrent ? 'Skryť heslo' : 'Zobraziť heslo'}
                  tabIndex={-1}
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* New password */}
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">
                Nové heslo
              </label>
              <div className="relative">
                <input
                  id="new-password"
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-3 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  autoComplete="new-password"
                  minLength={6}
                  data-testid="new-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  aria-label={showNew ? 'Skryť heslo' : 'Zobraziť heslo'}
                  tabIndex={-1}
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Aspoň 6 znakov.</p>
            </div>

            {/* Confirm new password */}
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                Potvrdenie nového hesla
              </label>
              <div className="relative">
                <input
                  id="confirm-password"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-3 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  autoComplete="new-password"
                  minLength={6}
                  data-testid="confirm-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  aria-label={showConfirm ? 'Skryť heslo' : 'Zobraziť heslo'}
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm"
                data-testid="password-change-error"
                role="alert"
              >
                {error}
              </div>
            )}

            {success && (
              <div
                className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm"
                data-testid="password-change-success"
                role="status"
              >
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              data-testid="password-change-submit"
            >
              {loading ? 'Ukladám…' : 'Zmeniť heslo'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AccountSettings
